import { createContext, useContext, useMemo, useRef, useState } from 'react';
import { UNINITIALIZED, SUCCEEDED } from 'calling-integration-sdk-support/async-data/constants/AsyncStatuses';
import { HUBSPOT } from 'calling-internal-common/call-provider/constants/ProviderNames';
import CallingProvider from 'calling-internal-common/call-provider/records/CallingProvider';
import { isExtensionsRemoteEnabled } from 'calling-internal-common/utils/isExtensionsRemoteEnabled';
import { useSelectedCallProvider } from '../hooks/useSelectedCallProvider';
import { useProviderMenuTour } from '../hooks/useProviderMenuTour';
import { useBroadcastEventHandler } from 'calling-integration-cross-tab/hooks/useBroadcastEventHandler';
import { useOpenProviderMenuEvent } from '../hooks/useOpenProviderMenuEvent';
import { jsx as _jsx } from "react/jsx-runtime";
export const initialState = {
  endTour: () => {},
  isThirdPartyProviderSelected: false,
  selectedCallProviderData: {
    name: HUBSPOT,
    usesCallingWindow: true
  },
  selectedCallProviderStatus: UNINITIALIZED,
  setCallingPreferencesProvider: () => {},
  setShowProviderMenu: () => {},
  showProviderMenu: false,
  updateAsyncData: () => {},
  isRemoteEnabled: false,
  isUserPreferencesProviderUnset: false,
  isProviderMenuMountedRef: {
    current: false
  },
  tourCompletionStatus: null,
  openProviderMenu: () => {}
};
const SelectedProviderContext = /*#__PURE__*/createContext(initialState);
export function SelectedProviderContextProvider({
  children
}) {
  const [isUserPreferencesProviderUnset, setIsUserPreferencesProviderUnset] = useState(false);
  const isProviderMenuMountedRef = useRef(false);
  const {
    status: selectedCallProviderStatus,
    data: selectedCallProviderData,
    updateAsyncData,
    isThirdPartyProviderSelected,
    setCallingPreferencesProvider
  } = useSelectedCallProvider({
    setIsUserPreferencesProviderUnset
  });
  const {
    endTour,
    setShowProviderMenu,
    showProviderMenu,
    tourCompletionStatus,
    openProviderMenu
  } = useProviderMenuTour();
  useOpenProviderMenuEvent({
    openProviderMenu,
    showProviderMenu,
    setIsUserPreferencesProviderUnset,
    selectedCallProviderData
  });
  const isRemoteEnabled = useMemo(() => {
    return isExtensionsRemoteEnabled(new CallingProvider(selectedCallProviderData));
  }, [selectedCallProviderData]);
  useBroadcastEventHandler({
    onUpdateCallProvider: message => {
      updateAsyncData({
        data: message.payload,
        status: SUCCEEDED
      });
      setShowProviderMenu(false);
    }
  });
  const selectedProviderInfo = useMemo(() => ({
    endTour,
    isThirdPartyProviderSelected,
    selectedCallProviderData,
    selectedCallProviderStatus,
    setCallingPreferencesProvider,
    setShowProviderMenu,
    showProviderMenu,
    updateAsyncData,
    isRemoteEnabled,
    isUserPreferencesProviderUnset,
    isProviderMenuMountedRef,
    tourCompletionStatus,
    openProviderMenu
  }), [endTour, isThirdPartyProviderSelected, selectedCallProviderData, selectedCallProviderStatus, setCallingPreferencesProvider, setShowProviderMenu, showProviderMenu, updateAsyncData, isRemoteEnabled, isUserPreferencesProviderUnset, isProviderMenuMountedRef, tourCompletionStatus, openProviderMenu]);
  return /*#__PURE__*/_jsx(SelectedProviderContext.Provider, {
    value: selectedProviderInfo,
    children: children
  });
}
export const useSelectedProviderContext = () => useContext(SelectedProviderContext);